import { useContext, useEffect, useState, type ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import $ from "jquery";
import UserContext from "../utils/UserContext";
import UrlNames from "../constants/UrlNames";
import { getDataFromCookies } from "../utils/CommonFunction";
import {
  getCategories,
  getCollarVideos,
  getCoursesByCategory,
  getGrades,
  getSubjectsByGrade,
} from "../services/coursesService";
import type {
  Course,
  CourseCardData,
  IdTitle,
  Subject,
} from "../types/courses";

const PAGE_TITLE = "Courses";

type SelectChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => void;

interface CoursesUserContext {
  satCourseData: Course[];
}

export interface CoursesState {
  isLoading: boolean;
  token: string | null;
  categories: IdTitle[];
  courses: Course[];
  grades: IdTitle[];
  subjects: Subject[];
  videos: CourseCardData[];
  categoryId: number;
  courseId: number;
  gradeId: number;
  subjectId: number;
  courseName: string;
  loader: boolean;
  refresh: () => void;
  onCategoryChange: SelectChangeHandler;
  onTypeChange: SelectChangeHandler;
  onGradeChange: SelectChangeHandler;
  onSubjectChange: SelectChangeHandler;
}

const parseParam = (value: string | null): number => {
  const parsed = parseInt(value ?? "", 10);
  return parsed ? parsed : -2;
};

/** Toggle the global course-nav highlight (legacy jQuery side-effect). */
const refreshActiveNav = (): void => {
  $(".courses-li").removeClass("active");
  $(".courses-li").addClass("active");
};

/**
 * Encapsulates the Courses screen: the cascading Category → Type/Grade →
 * Subject filters and the resulting video/chapter list.
 *
 * The five effects and their dependency arrays mirror the original screen
 * exactly (they read sibling state through closures), so the cascade timing is
 * preserved.
 */
export const useCourses = (
  token: string | null,
  loaderStatus: (status: boolean) => void
): CoursesState => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { satCourseData } = useContext(
    UserContext
  ) as unknown as CoursesUserContext;
  const { cookieSubscribeId } = getDataFromCookies();

  const [categories, setCategories] = useState<IdTitle[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [grades, setGrades] = useState<IdTitle[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [videos, setVideos] = useState<CourseCardData[]>([]);
  const [courseName, setCourseName] = useState("");
  const [loader, setLoader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subjectId, setSubjectId] = useState(-2);
  const [categoryId, setCategoryId] = useState(() =>
    parseParam(searchParams.get("category"))
  );
  const [courseId, setCourseId] = useState(() =>
    parseParam(searchParams.get("id"))
  );
  const [gradeId, setGradeId] = useState(() =>
    parseParam(searchParams.get("grade"))
  );

  const setCourseTitle = (
    id: number | string | null,
    isCourse: boolean,
    list: Course[] | Subject[]
  ): void => {
    try {
      if (!isCourse) {
        const match = (list as Subject[]).filter(
          (x) => String(x.id) === String(id)
        )[0];
        setCourseName(match.subject.title);
      } else {
        const match = (list as Course[]).filter(
          (x) => String(x.id) === String(id)
        )[0];
        setCourseName(match.title);
      }
    } catch {
      setLoader(false);
    }
  };

  const loadGrades = async (): Promise<void> => {
    const data = await getGrades();
    setGrades(data.response);
  };

  const loadVideos = async (
    id: number | string | null,
    courseList: Course[]
  ): Promise<void> => {
    const data = await getCollarVideos(id, cookieSubscribeId);
    if (data.status === 200) {
      setVideos(data.response);
      setCourseTitle(id, true, courseList);
    }
    setLoader(false);
  };

  const loadChapters = (id: number, subjectList: Subject[]): void => {
    if (subjectList.length > 0) {
      const chapters = subjectList
        .filter((item) => item.id === Number(id))
        .map((item) => item.chapters)[0];
      setVideos(chapters ?? []);
      setCourseTitle(id, false, subjectList);
    }
    setLoader(false);
  };

  const loadSubjects = async (id: number): Promise<void> => {
    const data = await getSubjectsByGrade(id, cookieSubscribeId);
    if (data.status === 200) {
      setSubjects(data.response);
      if (subjectId < 0) {
        setSubjectId(data.response[0].id);
      }
    }
  };

  const loadCategories = async (): Promise<void> => {
    try {
      const data = await getCategories();
      setCategories(data.response);
    } catch (error) {
      console.warn("get cat courses error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCoursesByCategory = async (id: number): Promise<void> => {
    const data = await getCoursesByCategory(id, cookieSubscribeId);
    if (data.status === 200) {
      setCourses(data.response);
      void loadVideos(searchParams.get("id"), data.response);
    } else {
      setCourses([]);
    }
  };

  const initializeData = (): void => {
    const category = parseInt(searchParams.get("category") ?? "", 10);
    const id = parseInt(searchParams.get("id") ?? "", 10);
    const grade = parseInt(searchParams.get("grade") ?? "", 10);

    if (category && id) {
      void loadCategories();
      if (category > -1) {
        void loadCoursesByCategory(category);
      } else {
        void loadGrades();
        setSubjects([]);
        void loadSubjects(gradeId);
      }
    } else if (category && grade) {
      void loadCategories();
      void loadGrades();
      setSubjects([]);
      void loadSubjects(gradeId);
    }
  };

  useEffect(() => {
    document.title = PAGE_TITLE;
    // Suppress the app-wide overlay; this screen renders its own skeleton.
    loaderStatus(true);
    if (token === null) {
      return;
    }
    const category = parseInt(searchParams.get("category") ?? "", 10);
    const id = parseInt(searchParams.get("id") ?? "", 10);
    const grade = parseInt(searchParams.get("grade") ?? "", 10);
    if ((category && id) || (category && grade)) {
      initializeData();
    } else {
      // Nothing to load from the URL — show the (empty) filter UI immediately.
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (subjectId > -1 && token !== null) {
      loadChapters(subjectId, subjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects, token]);

  useEffect(() => {
    if (categoryId > -1 && courseId > 0 && courses.length > 0 && token !== null) {
      void loadVideos(courseId, courses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, token]);

  useEffect(() => {
    if (categoryId > -1 && courseId > -1) {
      setSubjectId(-2);
      if (token !== null) {
        void loadCoursesByCategory(categoryId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, token]);

  useEffect(() => {
    if (subjectId > -1 && gradeId > -1) {
      setLoader(true);
      if (token !== null) {
        void loadSubjects(gradeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, token]);

  const onCategoryChange: SelectChangeHandler = (event) => {
    const value = parseInt(event.target.value, 10);
    setCourseId(-2);
    setSubjectId(-2);
    setGradeId(-2);
    if (value === -3) {
      setCategoryId(value);
      setCourses(satCourseData ?? []);
    } else if (value > -1) {
      setCategoryId(value);
      void loadCoursesByCategory(value);
      setSearchParams({ category: String(value) });
    } else {
      setSearchParams({ category: String(value) });
      setSubjects([]);
      setCategoryId(value);
      void loadGrades();
      if (gradeId && gradeId > -1) {
        void loadSubjects(gradeId);
      }
    }
    refreshActiveNav();
  };

  const onTypeChange: SelectChangeHandler = (event) => {
    if (event.target.value === "1" && categoryId === -3) {
      navigate(UrlNames.chapters);
      return;
    }
    const value = parseInt(event.target.value, 10);
    if (value > -2) {
      setSearchParams({ id: String(value), category: String(categoryId) });
      setLoader(true);
      setCourseId(value);
      refreshActiveNav();
    }
  };

  const onGradeChange: SelectChangeHandler = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value > -2) {
      setCourseId(-2);
      setSearchParams({ grade: String(value), category: String(categoryId) });
      setGradeId(value);
      setSubjects([]);
      void loadSubjects(value);
      refreshActiveNav();
    }
  };

  const onSubjectChange: SelectChangeHandler = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value > -2) {
      setLoader(true);
      setSubjectId(value);
      refreshActiveNav();
    }
  };

  // Preserved from the original (its body was entirely commented out).
  const refresh = (): void => {};

  return {
    isLoading,
    token,
    categories,
    courses,
    grades,
    subjects,
    videos,
    categoryId,
    courseId,
    gradeId,
    subjectId,
    courseName,
    loader,
    refresh,
    onCategoryChange,
    onTypeChange,
    onGradeChange,
    onSubjectChange,
  };
};
